import React, { useState } from "react"
import { UploadFileSvg } from "../../../svgFiles/UploadFileSvg"
import { ButtonSearchSvg } from "../../../svgFiles/ButtonSearchSvg"
import Button from "../buttons/button"
import { CircleCheckSvg } from "../../../svgFiles/CircleCheckSvg"
import { UndoSvg } from "../../../svgFiles/UndoSvg"
import { ErrorSvg } from "../../../svgFiles/ErrorSvg"
import { FolderSvg } from "../../../svgFiles/FolderSvg"
import { TemplateSvg } from "../../../svgFiles/TemplateSvg"
import * as XLSX from "xlsx";

const formatMap = {
    image: "image/png, image/jpeg",
    pdf: "application/pdf",
    xls: "application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
}

const errorMessages = {
    image: "Only PNG or JPEG images are allowed.",
    pdf: "Only PDF files are allowed.",
    xls: "Only Excel files (.xls, .xlsx) are allowed.",
}

const FileUploadBox = ({
    setUploadedFile,
    type = "file", // image, pdf, xls etc.
    acceptedFormats,
    maxSize = 10,
    onFileSelect,
    label = "Click to upload or drag and drop",
    subLabel = "Supported formats",
    tracking = false,
    order = false
}) => {
    const [file, setFile] = useState(null)
    const [error, setError] = useState("")

    // fallback to map if acceptedFormats not given
    const formats = acceptedFormats || formatMap[type] || "*/*"


    const handleChange = (e) => {
        const selected = e.target.files[0]
        if (!selected) return

        setError("")

        const name = selected.name.toLowerCase();
        if (selected.type === "image/gif" || name.endsWith(".gif")) {
            setError("GIF files are not allowed. Upload a PDF, PNG, or JPEG.");
            return
        }

        // check file type by MIME and extension
        let isValidType = false;
        if (formats) {
            const mimeTypes = formats.split(",").map(f => f.trim());
            isValidType = mimeTypes.includes(selected.type);
        }
        // fallback: check extension
        if (!isValidType) {
            const ext = selected.name.split('.').pop().toLowerCase();
            if (type === "file" && ["png", "jpg", "jpeg"].includes(ext)) isValidType = true;
            if (type === "file" && ext === "pdf") isValidType = true;
            if (type === "file" && ext === "docx") isValidType = true;
            if (type === "file" && ["xls", "xlsx"].includes(ext)) isValidType = true;
        }
        if (!isValidType) {
            setError(errorMessages[type] || "Invalid file type selected.")
            return
        }

        // check size
        if (selected.size / 1024 / 1024 > maxSize) {
            setError(`File exceeds ${maxSize} MB limit.`)
            return
        }

        setUploadedFile(selected)
        setFile(selected)
        onFileSelect && onFileSelect(selected)
    }

    const handleReupload = () => {
        setFile(null)
        setUploadedFile(null)
        setError("")
    }

    // Download template file handler
    const handleDownloadUploadedFile = () => {
        const headers = ["Order Id", "Tracking Number", "Shipping Date"];

        // Clean headers just in case of extra spaces
        const cleanHeaders = headers.map(h => h.trim());

        const worksheet = XLSX.utils.aoa_to_sheet([cleanHeaders]);
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
        XLSX.writeFile(workbook, "upload_tracking_number_sheet_template.xlsx");
    };

    const handleDownloadOrderTemplate = () => {
        const headers = ["Order Id"];

        // Clean headers just in case of extra spaces
        const cleanHeaders = headers.map(h => h.trim());
        const worksheet = XLSX.utils.aoa_to_sheet([cleanHeaders]);
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
        XLSX.writeFile(workbook, "upload_order_number_sheet_template.xlsx");
    };

    return (
        <>
            {!file && !error ? (
                <>
                    {tracking || order ?
                        <div className="uploadFile d-flex gap-3 flex-column justify-content-between overflow-hidden align-items-center uploadTracking">
                            <input
                                type="file"
                                accept={formats}
                                onChange={handleChange}
                                id="file-upload-input"
                            />
                            <span className="text-center">
                                Drag and drop a file here, or:
                            </span>

                            <div className="d-flex gap-5 align-items-center justify-content-center">
                                <div className="text-center">
                                    <FolderSvg />
                                    <label htmlFor="upload" className="d-block mt-1">Upload</label>
                                </div>
                                <div className="text-center position-relative z-3">
                                    <TemplateSvg onClick={order ? handleDownloadOrderTemplate : handleDownloadUploadedFile} />
                                    <label htmlFor="template" className="d-block mt-1">Template</label>
                                </div>
                            </div>
                        </div>

                        :
                        <div className="uploadFile d-flex gap-3 flex-column justify-content-between overflow-hidden align-items-center">
                            <input
                                type="file"
                                accept={formats}
                                onChange={handleChange}
                                id="file-upload-input"
                            />
                            <label htmlFor="file-upload-input" style={{ cursor: "pointer" }}>
                                <UploadFileSvg />
                            </label>
                            <span className="text-center">
                                {label} <br />
                                {subLabel}
                            </span>

                            <label htmlFor="file-upload-input">
                                <Button
                                    size={"xs"}
                                    label={"Browse file"}
                                    icon={<ButtonSearchSvg />}
                                    className={"p-0 px-3"}
                                />
                            </label>
                        </div>
                    }
                </>
            ) : (
                <div className="d-flex flex-column align-items-center text-center gap-2 uploadFile file-view">
                    {error ? <ErrorSvg /> : <CircleCheckSvg className={error ? "text-danger" : "text-success"} />}
                    {error ? (
                        <p className="text-danger small m-0 uploadFile__message">{error}</p>
                    ) : (
                        <p className="text-success m-0 uploadFile__message">
                            <span className="uploadFile__message-line">
                                File:{" "}
                                <strong className="uploadFile__filename" title={file.name}>
                                    {file.name}
                                </strong>
                            </span>
                            <span className="uploadFile__message-line">
                                uploaded successfully!
                            </span>
                        </p>
                    )}

                    <Button
                        icon={<UndoSvg />}
                        size={"sm"}
                        label={"Re-upload"}
                        className={"cancel bg-white cancel-upload"}
                        onClick={handleReupload}
                    />
                </div>
            )}
        </>
    )
}

export default FileUploadBox
